import React, { useState, useEffect, useCallback } from "react";
import { 
  FaCalendarAlt, 
  FaSearch, 
  FaDownload, 
  FaFilter, 
  FaSchool, 
  FaUser, 
  FaClock, 
  FaChartPie, 
  FaCheckCircle, 
  FaTimesCircle, 
  FaSync, 
  FaRegFileAlt 
} from "react-icons/fa";
import { getClasses } from "../../services/classService";
import { getStudents } from "../../services/studentService";
import { getAttendanceReports } from "../../services/schoolService";
import "../teacher/TeacherLayer.css";

const AttendanceReportsLayer = () => {
  const [activeTab, setActiveTab] = useState("daily"); // "daily" | "monthly" | "student"
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Date Focus States for dd/mm/yyyy toggling
  const [dailyFocused, setDailyFocused] = useState(false);
  const [fromFocused, setFromFocused] = useState(false);
  const [toFocused, setToFocused] = useState(false);

  // Filters State
  const [selectedClassId, setSelectedClassId] = useState("");
  const [dailyDate, setDailyDate] = useState(new Date().toISOString().split("T")[0]);
  const [monthlyMonth, setMonthlyMonth] = useState(new Date().toISOString().substring(0, 7)); // "YYYY-MM"
  
  // Student History Filters
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [studentFromDate, setStudentFromDate] = useState(
    new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split("T")[0]
  ); // 7 days ago
  const [studentToDate, setStudentToDate] = useState(new Date().toISOString().split("T")[0]);

  // Search filter for local list filtering
  const [searchText, setSearchText] = useState("");

  // API Response States
  const [classesData, setClassesData] = useState([]);
  const [studentProfile, setStudentProfile] = useState(null);
  const [summaryStats, setSummaryStats] = useState(null);
  const [studentHistory, setStudentHistory] = useState([]);

  // Fetch classes and students list on mount
  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const classRes = await getClasses();
        if (classRes?.status) {
          setClasses(classRes.data || []);
        }
        const studentRes = await getStudents("?limit=9999");
        if (studentRes?.status) {
          setStudents(studentRes.data || []);
        }
      } catch (error) {
        console.error("Error loading dropdown data", error);
      }
    };
    fetchFilters();
  }, []);

  // Fetch reports data based on tab & filters
  const fetchReport = useCallback(async () => {
    setLoading(true);
    setClassesData([]);
    setStudentProfile(null);
    setSummaryStats(null);
    setStudentHistory([]);

    try {
      if (activeTab === "daily") {
        const params = {
          action: "class_view",
          from_date: dailyDate,
          to_date: dailyDate
        };
        if (selectedClassId) {
          params.class_id = selectedClassId;
        }
        const res = await getAttendanceReports(params);
        if (res?.status && res?.classes_data) {
          setClassesData(res.classes_data);
        }
      } else if (activeTab === "monthly") {
        const params = {
          action: "class_view"
        };
        if (selectedClassId) {
          params.class_id = selectedClassId;
        }
        // Calculate start and end date of selected month
        const [year, month] = monthlyMonth.split("-");
        const fromDateStr = `${year}-${month}-01`;
        const toDateStr = new Date(year, month, 0).toISOString().split("T")[0];
        params.from_date = fromDateStr;
        params.to_date = toDateStr;

        const res = await getAttendanceReports(params);
        if (res?.status && res?.classes_data) {
          setClassesData(res.classes_data);
        }
      } else if (activeTab === "student") {
        if (!selectedStudentId) {
          setLoading(false);
          return;
        }
        const params = {
          action: "student_history",
          student_id: selectedStudentId,
          from_date: studentFromDate,
          to_date: studentToDate
        };
        const res = await getAttendanceReports(params);
        if (res?.status) {
          setStudentProfile(res.student_profile || null);
          setSummaryStats(res.summary_stats || null);
          setStudentHistory(res.history || []);
        }
      }
    } catch (error) {
      console.error("Error loading attendance report", error);
    } finally {
      setLoading(false);
    }
  }, [activeTab, selectedClassId, dailyDate, monthlyMonth, selectedStudentId, studentFromDate, studentToDate]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  // Export CSV Helper
  const handleExport = () => {
    let headers = [];
    let rows = [];
    let filename = `attendance_report_${activeTab}.csv`;

    if (activeTab === "student" && studentProfile) {
      headers = ["Date", "Status", "Check In", "Check Out", "Method", "Reason"];
      rows = studentHistory.map(h => [
        h.date,
        h.status,
        h.clock_in || "-",
        h.clock_out || "-",
        h.attendance_type || "-",
        h.absent_reason || "-"
      ]);
      filename = `attendance_history_${studentProfile.name.replace(/\s+/g, "_")}.csv`;
    } else if (selectedClassId) {
      headers = ["Date", "Roll No", "Student Name", "Gender", "Status", "Clock In", "Clock Out", "Type", "Reason"];
      const classData = classesData[0];
      if (classData && classData.students_history) {
        rows = classData.students_history
          .filter(s => s.name?.toLowerCase().includes(searchText.toLowerCase()))
          .map(s => [
            s.date,
            s.roll_number || "-",
            s.name,
            s.gender || "-",
            s.attendance_status,
            s.clock_in || "-",
            s.clock_out || "-",
            s.type || "-",
            s.reason || "-"
          ]);
        filename = `${classData.class_name}_attendance_${dailyDate}.csv`;
      }
    } else {
      headers = ["Class Name", "Total Students", "Present", "Absent", "Late", "Half Day", "Leave", "Not Marked", "Status"];
      rows = classesData
        .filter(c => c.class_name?.toLowerCase().includes(searchText.toLowerCase()))
        .map(c => {
          const sc = c.summary_counters || {};
          const isSubmitted = (sc.not_marked || 0) === 0;
          return [
            c.class_name,
            sc.total_students_matched || 0,
            sc.present || 0,
            sc.absent || 0,
            sc.late || 0,
            sc.half_day || 0,
            sc.leave || 0,
            sc.not_marked || 0,
            isSubmitted ? "Submitted" : "Pending"
          ];
        });
    }

    const csvContent = [
      headers.join(","),
      ...rows.map(e => e.map(val => `"${String(val).replace(/"/g, '""')}"`).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="school-wrapper">
      <div className="school-card smooth-card">
        <div className="admin-page-shell">
          
          {/* HEADER */}
          <div className="admin-page-header list-header flex-wrap">
            <div>
              <h5 className="admin-page-title">Attendance Reports</h5>
              <p className="text-muted mt-1 mb-0" style={{ fontSize: "14px" }}>
                Filter and view comprehensive daily, monthly summaries and individual student histories
              </p>
            </div>
            <div className="d-flex align-items-center gap-2">
              <button 
                className="btn btn-outline-primary d-inline-flex align-items-center gap-2 mt-2 mt-sm-0"
                onClick={handleExport}
                disabled={loading || (activeTab === "student" && !studentProfile) || (!selectedClassId && classesData.length === 0)}
              >
                <FaDownload /> Export CSV
              </button>
              <button 
                className="btn btn-light border d-flex align-items-center justify-content-center p-2 rounded-circle" 
                onClick={fetchReport} 
                style={{ width: '40px', height: '40px' }} 
                title="Refresh"
              >
                <FaSync className="text-secondary" />
              </button>
            </div>
          </div>

          {/* TABS NAVIGATION */}
          <div className="mt-4 border-bottom">
            <ul className="nav nav-tabs border-0 gap-3">
              <li className="nav-item">
                <button 
                  className={`nav-link border-0 fw-semibold px-4 pb-3 ${activeTab === 'daily' ? 'active text-primary border-bottom border-primary border-3' : 'text-muted'}`}
                  onClick={() => {
                    setActiveTab('daily');
                    setSelectedClassId("");
                  }}
                  style={{ background: 'transparent' }}
                >
                  Daily Report
                </button>
              </li>
              <li className="nav-item">
                <button 
                  className={`nav-link border-0 fw-semibold px-4 pb-3 ${activeTab === 'monthly' ? 'active text-primary border-bottom border-primary border-3' : 'text-muted'}`}
                  onClick={() => {
                    setActiveTab('monthly');
                    setSelectedClassId("");
                  }}
                  style={{ background: 'transparent' }}
                >
                  Monthly Summary
                </button>
              </li>
              <li className="nav-item">
                <button 
                  className={`nav-link border-0 fw-semibold px-4 pb-3 ${activeTab === 'student' ? 'active text-primary border-bottom border-primary border-3' : 'text-muted'}`}
                  onClick={() => setActiveTab('student')}
                  style={{ background: 'transparent' }}
                >
                  Student History
                </button>
              </li>
            </ul>
          </div>

          {/* FILTERS PANEL */}
          <div className="bg-light p-3 rounded-3 mb-4 mt-4 border" style={{ borderColor: '#e9ecef' }}>
            <div className="row g-3 align-items-end">
              
              {/* Daily Tab Date */}
              {activeTab === "daily" && (
                <div className="col-12 col-md-4 col-lg-3">
                  <label className="fw-semibold text-muted d-block mb-1" style={{ fontSize: '13px' }}><FaCalendarAlt className="me-1" /> Select Date</label>
                  <input 
                    type={dailyFocused ? "date" : "text"} 
                    placeholder="dd/mm/yyyy"
                    className="form-control" 
                    value={dailyFocused ? dailyDate : (dailyDate ? dailyDate.split("-").reverse().join("/") : "")}
                    onFocus={() => setDailyFocused(true)}
                    onBlur={() => setDailyFocused(false)}
                    onChange={(e) => setDailyDate(e.target.value)}
                  />
                </div>
              )}

              {/* Monthly Tab Month */}
              {activeTab === "monthly" && (
                <div className="col-12 col-md-4 col-lg-3">
                  <label className="fw-semibold text-muted d-block mb-1" style={{ fontSize: '13px' }}><FaCalendarAlt className="me-1" /> Select Month</label>
                  <input 
                    type="month" 
                    className="form-control" 
                    value={monthlyMonth} 
                    onChange={(e) => setMonthlyMonth(e.target.value)}
                  />
                </div>
              )}

              {/* Student Tab Student Selector */}
              {activeTab === "student" && (
                <div className="col-12 col-md-4 col-lg-3">
                  <label className="fw-semibold text-muted d-block mb-1" style={{ fontSize: '13px' }}><FaUser className="me-1" /> Select Student</label>
                  <select 
                    className="form-select"
                    value={selectedStudentId}
                    onChange={(e) => setSelectedStudentId(e.target.value)}
                  >
                    <option value="">-- Choose Student --</option>
                    {students.map((st) => (
                      <option key={st.id} value={st.id}>
                        {st.name} {st.roll_number ? `(${st.roll_number})` : ""}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Student Tab From Date */}
              {activeTab === "student" && (
                <div className="col-12 col-sm-6 col-md-4 col-lg-3">
                  <label className="fw-semibold text-muted d-block mb-1" style={{ fontSize: '13px' }}>From Date</label>
                  <input 
                    type={fromFocused ? "date" : "text"} 
                    placeholder="dd/mm/yyyy"
                    className="form-control" 
                    value={fromFocused ? studentFromDate : (studentFromDate ? studentFromDate.split("-").reverse().join("/") : "")}
                    onFocus={() => setFromFocused(true)}
                    onBlur={() => setFromFocused(false)}
                    onChange={(e) => setStudentFromDate(e.target.value)}
                  />
                </div>
              )}

              {/* Student Tab To Date */}
              {activeTab === "student" && (
                <div className="col-12 col-sm-6 col-md-4 col-lg-3">
                  <label className="fw-semibold text-muted d-block mb-1" style={{ fontSize: '13px' }}>To Date</label>
                  <input 
                    type={toFocused ? "date" : "text"} 
                    placeholder="dd/mm/yyyy"
                    className="form-control" 
                    value={toFocused ? studentToDate : (studentToDate ? studentToDate.split("-").reverse().join("/") : "")}
                    onFocus={() => setToFocused(true)}
                    onBlur={() => setToFocused(false)}
                    onChange={(e) => setStudentToDate(e.target.value)}
                  />
                </div>
              )}

              {/* Daily & Monthly Class Filter */}
              {(activeTab === "daily" || activeTab === "monthly") && (
                <div className="col-12 col-md-4 col-lg-3">
                  <label className="fw-semibold text-muted d-block mb-1" style={{ fontSize: '13px' }}><FaSchool className="me-1" /> Class</label>
                  <select 
                    className="form-select" 
                    value={selectedClassId} 
                    onChange={(e) => setSelectedClassId(e.target.value)}
                  >
                    <option value="">All Classes</option>
                    {classes.map((cls) => (
                      <option key={cls.id} value={cls.id}>{cls.class_name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Local search filter when we have a grid to filter */}
              {activeTab !== "student" && (
                <div className="col-12 col-md-4 col-lg-3">
                  <label className="fw-semibold text-muted d-block mb-1" style={{ fontSize: '13px' }}><FaSearch className="me-1" /> Quick Search</label>
                  <input 
                    type="text" 
                    placeholder="Search by name..." 
                    className="form-control"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                </div>
              )}

            </div>
          </div>

          {/* STUDENT PROFILE CARD (Only in Student Tab) */}
          {activeTab === "student" && studentProfile && (
            <div className="mb-4">
              <div className="card shadow-sm border-0 p-3 mb-3 bg-white" style={{ borderRadius: "12px" }}>
                <div className="row align-items-center">
                  <div className="col-auto">
                    <div 
                      className="d-flex align-items-center justify-content-center text-white fw-bold rounded-circle"
                      style={{ width: "50px", height: "50px", background: "linear-gradient(135deg, #6a3cff, #4318ff)" }}
                    >
                      {studentProfile.name.charAt(0).toUpperCase()}
                    </div>
                  </div>
                  <div className="col">
                    <h5 className="fw-bold text-dark mb-0">{studentProfile.name}</h5>
                    <div className="text-secondary small mt-1">
                      <span className="me-3"><strong>Roll No:</strong> {studentProfile.roll_number || "N/A"}</span>
                      <span className="me-3"><strong>Gender:</strong> {studentProfile.gender || "N/A"}</span>
                      <span><strong>Email:</strong> {studentProfile.email || "N/A"}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Stats Counters */}
              {summaryStats && (
                <div className="row g-3 mb-4">
                  <div className="col-6 col-sm-3">
                    <div className="p-3 border rounded text-center" style={{ background: "#ECFDF5", borderColor: "#A7F3D0" }}>
                      <div className="small fw-semibold text-success uppercase">Present</div>
                      <h4 className="fw-extrabold text-success mb-0 mt-1">{summaryStats.Present || 0}</h4>
                    </div>
                  </div>
                  <div className="col-6 col-sm-3">
                    <div className="p-3 border rounded text-center" style={{ background: "#FEF2F2", borderColor: "#FCA5A5" }}>
                      <div className="small fw-semibold text-danger uppercase">Absent</div>
                      <h4 className="fw-extrabold text-danger mb-0 mt-1">{summaryStats.Absent || 0}</h4>
                    </div>
                  </div>
                  <div className="col-6 col-sm-3">
                    <div className="p-3 border rounded text-center" style={{ background: "#FFFBEB", borderColor: "#FDE68A" }}>
                      <div className="small fw-semibold text-warning uppercase">Late</div>
                      <h4 className="fw-extrabold text-warning mb-0 mt-1">{summaryStats.Late || 0}</h4>
                    </div>
                  </div>
                  <div className="col-6 col-sm-3">
                    <div className="p-3 border rounded text-center" style={{ background: "#EFF6FF", borderColor: "#BFDBFE" }}>
                      <div className="small fw-semibold text-blue uppercase">Half Day</div>
                      <h4 className="fw-extrabold text-blue mb-0 mt-1">{summaryStats["Half Day"] || 0}</h4>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TABLES VIEW SHELL */}
          <div className="teacher-table-shell admin-table-card admin-table-scroll admin-responsive-table">
            
            {loading ? (
              <div className="text-center text-muted py-5">
                <div className="spinner-border text-primary spinner-border-sm me-2" role="status"></div>
                Loading report data...
              </div>
            ) : activeTab === "student" ? (
              // Student History Table
              <table className="user-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Check In</th>
                    <th>Check Out</th>
                    <th>Punch Type</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {!selectedStudentId ? (
                    <tr>
                      <td colSpan="6" className="text-center text-muted py-4 fw-medium">
                        Please select a student from the dropdown above to view history.
                      </td>
                    </tr>
                  ) : studentHistory.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center text-muted py-4 fw-medium">
                        No history records found for the selected date range.
                      </td>
                    </tr>
                  ) : (
                    studentHistory.map((item, idx) => (
                      <tr key={idx} className="smooth-table-row">
                        <td className="fw-semibold text-dark">{item.date}</td>
                        <td>
                          <span className={`badge rounded-pill px-3 py-1 ${
                            item.status === 'Present' ? 'bg-success bg-opacity-10 text-success border border-success border-opacity-25' : 
                            item.status === 'Absent' ? 'bg-danger bg-opacity-10 text-danger border border-danger border-opacity-25' :
                            item.status === 'Late' ? 'bg-warning bg-opacity-10 text-warning border border-warning border-opacity-25' :
                            'bg-info bg-opacity-10 text-info border border-info border-opacity-25'
                          }`}>
                            {item.status}
                          </span>
                        </td>
                        <td className="text-secondary fw-medium">{item.clock_in || "-"}</td>
                        <td className="text-secondary fw-medium">{item.clock_out || "-"}</td>
                        <td>
                          <span className="badge bg-light text-secondary border px-2 py-0.5" style={{ fontSize: "11px" }}>
                            {item.attendance_type || "N/A"}
                          </span>
                        </td>
                        <td className="text-muted small" style={{ maxWidth: "200px", wordBreak: "break-word" }}>
                          {item.absent_reason || "-"}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            ) : selectedClassId ? (
              // Specific Class Detailed Student Attendance records
              <table className="user-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Roll No</th>
                    <th>Student Name</th>
                    <th>Gender</th>
                    <th>Status</th>
                    <th>Check In</th>
                    <th>Check Out</th>
                    <th>Method</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {classesData.length === 0 || !classesData[0].students_history || classesData[0].students_history.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="text-center text-muted py-4 fw-medium">
                        No student attendance records found for this class & date period.
                      </td>
                    </tr>
                  ) : (
                    classesData[0].students_history
                      .filter(s => s.name?.toLowerCase().includes(searchText.toLowerCase()))
                      .map((s, idx) => (
                        <tr key={idx} className="smooth-table-row">
                          <td className="text-muted fw-medium">{s.date}</td>
                          <td className="fw-bold text-secondary">#{s.roll_number || "N/A"}</td>
                          <td>
                            <div className="fw-bold text-dark">{s.name}</div>
                            {s.email && <div className="text-muted" style={{ fontSize: "11px" }}>{s.email}</div>}
                          </td>
                          <td>
                            <span className="badge bg-light text-dark border px-2 py-1" style={{ fontSize: "11.5px" }}>
                              {s.gender || "Other"}
                            </span>
                          </td>
                          <td>
                            <span className={`badge rounded-pill px-3 py-1 ${
                              s.attendance_status === 'Present' ? 'bg-success bg-opacity-10 text-success border border-success border-opacity-25' : 
                              s.attendance_status === 'Absent' ? 'bg-danger bg-opacity-10 text-danger border border-danger border-opacity-25' :
                              s.attendance_status === 'Late' ? 'bg-warning bg-opacity-10 text-warning border border-warning border-opacity-25' :
                              s.attendance_status === 'Half Day' ? 'bg-info bg-opacity-10 text-info border border-info border-opacity-25' :
                              'bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-25'
                            }`}>
                              {s.attendance_status}
                            </span>
                          </td>
                          <td className="text-secondary fw-semibold">{s.clock_in || "-"}</td>
                          <td className="text-secondary fw-semibold">{s.clock_out || "-"}</td>
                          <td>
                            <span className="badge bg-light text-secondary border px-2 py-0.5" style={{ fontSize: "11px" }}>
                              {s.type || "N/A"}
                            </span>
                          </td>
                          <td className="text-muted small" style={{ maxWidth: "180px", wordBreak: "break-word" }}>
                            {s.reason || "-"}
                          </td>
                        </tr>
                      ))
                  )}
                </tbody>
              </table>
            ) : (
              // All Classes Summary Grid
              <table className="user-table">
                <thead>
                  <tr>
                    <th>Class Name</th>
                    <th>Total Students</th>
                    <th>Present</th>
                    <th>Absent</th>
                    <th>Late</th>
                    <th>Half Day</th>
                    <th>Leave</th>
                    <th>Not Marked</th>
                    <th>Status</th>
                    <th className="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {classesData.length === 0 ? (
                    <tr>
                      <td colSpan="10" className="text-center text-muted py-4 fw-medium">
                        No class summaries found for the selected filter criteria.
                      </td>
                    </tr>
                  ) : (
                    classesData
                      .filter(c => c.class_name?.toLowerCase().includes(searchText.toLowerCase()))
                      .map((cls) => {
                        const sc = cls.summary_counters || {};
                        const isSubmitted = (sc.not_marked || 0) === 0;
                        return (
                          <tr key={cls.class_id} className="smooth-table-row">
                            <td className="fw-bold text-primary">{cls.class_name}</td>
                            <td>{sc.total_students_matched || 0}</td>
                            <td className="text-success fw-bold">{sc.present || 0}</td>
                            <td className="text-danger fw-bold">{sc.absent || 0}</td>
                            <td className="text-warning fw-bold">{sc.late || 0}</td>
                            <td className="text-info fw-bold">{sc.half_day || 0}</td>
                            <td className="fw-bold" style={{ color: "#7e22ce" }}>{sc.leave || 0}</td>
                            <td className="text-secondary fw-bold">{sc.not_marked || 0}</td>
                            <td>
                              <span className={`badge rounded-pill px-3 py-1 ${isSubmitted ? 'bg-success bg-opacity-10 text-success border border-success border-opacity-25' : 'bg-warning bg-opacity-10 text-warning border border-warning border-opacity-25'}`}>
                                {isSubmitted ? "Submitted" : "Pending"}
                              </span>
                            </td>
                            <td className="text-center">
                              <button 
                                className="btn btn-sm btn-outline-primary rounded-pill px-3 py-1 fw-bold"
                                onClick={() => setSelectedClassId(String(cls.class_id))}
                              >
                                View Details
                              </button>
                            </td>
                          </tr>
                        );
                      })
                  )}
                </tbody>
              </table>
            )}

          </div>

        </div>
      </div>
    </div>
  );
};

export default AttendanceReportsLayer;
